import { createFeatureSelector, createSelector } from '@ngrx/store';
import { AuthState } from './auth.state';
import { decodeJwtClaims, isJwtExpired } from '../../core/auth/jwt';

export const selectAuthState = createFeatureSelector<AuthState>('auth');

export const selectIsAuthenticated = createSelector(
    selectAuthState,
    (state: AuthState) => state.isAuthenticated
);

export const selectCurrentUser = createSelector(
    selectAuthState,
    (state: AuthState) => state.user
);

export const selectAccessToken = createSelector(
    selectAuthState,
    (state: AuthState) => state.accessToken
);

export const selectActor = createSelector(
    selectAuthState,
    (state: AuthState) => state.actor
);

export const selectAuthLoading = createSelector(
    selectAuthState,
    (state: AuthState) => state.loading
);

export const selectAuthError = createSelector(
    selectAuthState,
    (state: AuthState) => state.error
);

export const selectLastAuthCheck = createSelector(
    selectAuthState,
    (state: AuthState) => state.lastAuthCheck
);

export const selectIsAuthCheckNeeded = createSelector(
    selectAuthState,
    (state: AuthState): boolean => {
        const claims = decodeJwtClaims(state.accessToken);
        const hasValidAccessToken = Boolean(state.accessToken && claims && !isJwtExpired(claims));
        if (hasValidAccessToken) return false;

        // If the token exists but is expired/invalid, we should try refresh (unless throttled by error rules below).
        const hasAnyAccessToken = Boolean(state.accessToken);

        const now = Date.now();
        const timeSinceLastCheck = state.lastAuthCheck ? now - state.lastAuthCheck : Infinity;

        // If we have an error and it's been less than 2 minutes, don't retry
        if (state.error && timeSinceLastCheck < 120000) { // 2 minutes
            return false;
        }

        // If authentication status is known and recent (less than 5 minutes), don't check
        const fiveMinutes = 5 * 60 * 1000;
        if (state.isAuthenticated !== null && timeSinceLastCheck < fiveMinutes) {
            return hasAnyAccessToken;
        }

        // Only check if we've never checked or it's been more than 5 minutes
        const needsCheck = state.isAuthenticated === null || timeSinceLastCheck > fiveMinutes;
        return needsCheck;
    }
);

export const selectAuthStatus = createSelector(
    selectAuthState,
    (state: AuthState) => ({
        isAuthenticated: state.isAuthenticated,
        accessToken: state.accessToken,
        loading: state.loading,
        error: state.error,
        user: state.user,
        actor: state.actor
    })
);
